// ============================================================================
// A submitted application, as an account manager reads it on the sub's profile
// (item 833, Ben 2026-10-13, Option 1).
//
// ⛔ THE FORM'S OWN SCHEMA IS THE ONLY SOURCE OF WORDING AND ORDER. Every
// question and every option label shown comes from INTAKE_TRANCHES in
// questions.ts, whose copy is final. Nothing here retypes or rewords one; if the
// form changes, this follows.
//
// The answers live only on the application record (sourcing_prospects.intake_json)
// and are read through from there. Nothing is copied onto the sub.
//
//  • Contact answers (name, business, phone, email) are left out — they are
//    already at the top of the profile — and so is the area question.
//  • "How they work": the multiple-choice answers, then the short typed one
//    ("Where did you hear about us?"), each in form order — the approved demo's
//    order.
//  • "In their own words": the long written answers, with the question's
//    explanation under it. Shown as the applicant wrote them, never translated.
//  • A choice answer reads in English whatever language the applicant used. A
//    value the form has no option for shows as it was stored; a blank one reads
//    "Not answered".
//  • An option the form marks `strike` is tagged "Counts against" here. The
//    applicant never sees that mark; the account manager does.
//  • An answer given to wording the form has since replaced reads through
//    RETIRED_OPTION_LABELS, marked "(earlier wording)". It counts nothing against:
//    the count follows the form as it is today (item 871, Ben 2026-10-19).
// ============================================================================

import { strikeCount } from './engine';
import { INTAKE_TRANCHES, type IntakeQuestion } from './questions';

export const NOT_ANSWERED = 'Not answered';

/**
 * Stored answers to an option the form no longer offers, as the profile shows them.
 * Q3 asked about main income until 2026-10-19; it now asks whether the business
 * serves other clients, under the same question id (item 871).
 */
export const RETIRED_OPTION_LABELS: Record<string, Record<string, string>> = {
  main_income: {
    main: 'Main business (earlier wording)',
    side: 'Side income (earlier wording)',
  },
};

export interface AnswerRow {
  id: string;
  /** The form's English prompt, verbatim. */
  question: string;
  /** The form's English explanation under a written question, verbatim. */
  help: string | null;
  /** What to show: the option's English label, the typed text, or "Not answered". */
  answer: string;
  answered: boolean;
  /** The chosen option is one the form counts against the applicant. */
  countsAgainst: boolean;
}

export interface ApplicationAnswers {
  howTheyWork: AnswerRow[];
  inTheirOwnWords: AnswerRow[];
  /** How many answers the form counts against the applicant. */
  countsAgainst: number;
}

function shownOnProfile(q: IntakeQuestion): boolean {
  return !q.contact && q.signal !== 'market';
}

function asAnswerMap(intakeJson: unknown): Record<string, string> {
  if (!intakeJson || typeof intakeJson !== 'object' || Array.isArray(intakeJson)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(intakeJson as Record<string, unknown>)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = String(v).trim();
  }
  return out;
}

function row(q: IntakeQuestion, raw: string | undefined): AnswerRow {
  const value = (raw ?? '').trim();
  const base = { id: q.id, question: q.prompt.en, help: q.type === 'long_text' ? (q.help?.en ?? null) : null };
  if (!value) return { ...base, answer: NOT_ANSWERED, answered: false, countsAgainst: false };
  const opt = q.options?.find((o) => o.value === value);
  const retired = opt ? undefined : RETIRED_OPTION_LABELS[q.id]?.[value];
  return { ...base, answer: opt ? opt.label.en : (retired ?? value), answered: true, countsAgainst: Boolean(opt?.strike) };
}

/** The application's answers, grouped and worded exactly as the form has them. */
export function applicationAnswers(intakeJson: unknown): ApplicationAnswers {
  const answers = asAnswerMap(intakeJson);
  const questions = INTAKE_TRANCHES.flatMap((t) => t.questions).filter(shownOnProfile);
  const isChoice = (q: IntakeQuestion) => q.type === 'choice' || q.type === 'number_choice';
  const howTheyWork = [
    ...questions.filter(isChoice),
    ...questions.filter((q) => q.type === 'text'),
  ].map((q) => row(q, answers[q.id]));
  const inTheirOwnWords = questions.filter((q) => q.type === 'long_text').map((q) => row(q, answers[q.id]));
  return {
    howTheyWork,
    inTheirOwnWords,
    // The form's own count (engine.strikeCount), never a second copy of the rule.
    countsAgainst: strikeCount(answers),
  };
}

/** Answer ids stored on an application that the form no longer asks — not shown. */
export function retiredAnswerIds(intakeJson: unknown): string[] {
  const known = new Set(INTAKE_TRANCHES.flatMap((t) => t.questions.map((q) => q.id)));
  return Object.keys(asAnswerMap(intakeJson)).filter((k) => !known.has(k));
}

/** The language the applicant answered in, as the profile says it. */
export function answeredInLabel(intakeLanguage: string | null | undefined): string | null {
  if (intakeLanguage === 'es') return 'Spanish';
  if (intakeLanguage === 'en') return 'English';
  return null;
}

/**
 * The AI fit score's band, in the scoring function's own words (sourcing-ai's
 * scoring guide: 80+ / 60–79 / 40–59 / under 40). TCSD-0494.
 */
export function fitBand(score: number): { tone: 'ok' | 'warn' | 'neutral' | 'danger'; label: string } {
  if (score >= 80) return { tone: 'ok', label: 'Ready for a hospital' };
  if (score >= 60) return { tone: 'warn', label: 'Promising — gaps to confirm in person' };
  if (score >= 40) return { tone: 'neutral', label: 'Mixed' };
  return { tone: 'danger', label: 'Poor fit' };
}

const PROVISIONAL = /^\[Provisional[^\]]*\]\s*/i;

/** The summary as the AI wrote it, with its "[Provisional …]" prefix lifted out into a flag. */
export function fitSummary(raw: string | null | undefined): { text: string; provisional: boolean } {
  const s = (raw ?? '').trim();
  return { text: s.replace(PROVISIONAL, ''), provisional: PROVISIONAL.test(s) };
}
